import { GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery } from '@/__generated__/graphql';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { PlusIcon } from '@/assets/icons/info-icons/plus';
import { OpenFileIcon } from '@/assets/static/icons/open-file';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import FileUpload from '@/components/basicComponents/fileUpload';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Tag from '@/components/basicComponents/tag';
import { UPDATE_LEGAL_ENTITY_DOCUMENTATION_MUTATION } from '@/components/investors-page/investorDetailsPage/tabs/documentationTab/queries';
import { Loader } from '@/components/loaders/loader';
import DropdownActions from '@/components/table/dropdownActions';
import { TableBodyWrapper, TableRowCell, TextCell } from '@/components/table/styles';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { processExpectedAttachments } from '@trace-8/trace8-schema';
import axios from 'axios';
import fileDownload from 'js-file-download';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { AttachmentSubTypesEnum } from '../../addAttachmentFileModalPage';
import DeleteAttachment from '../../addAttachmentFileModalPage/modals/deleteAttachment';
import { StyledHeaderComponent, ThreeColumnWrapper } from '../styles';
import { IDocumentationAttachment, IRenderTemplateRow, OpenWizardModalPageArgsType } from '../types';
import { evaluateDependencyItem, shouldRenderEmptyComponent } from '../utils';

interface IExpectedAttachments {
  name: string;
  label: string;
  attachments: string[];
}

interface IAttachmentSectionWithFiles {
  name: string;
  label: string;
  contactId?: string;
  initialExpectedAttachmentsCount: number;
  expectedAttachmentsCount: number;
  additionalAttachmentsCount: number;
  expectedAttachments: IExpectedAttachments[];
}

type AttachmentSection = {
  name: string;
  label: string;
  contactId?: string;
  expectedAttachments: Array<{
    name: string;
    label: string;
  }>;
};

enum InfoBannerTypeEnum {
  NoExpectedFiles = 'noExpectedFiles',
  MissingFiles = 'missingFiles',
  AllFilesPresent = 'allFilesPresent'
}

const infoBannerProperies = {
  [InfoBannerTypeEnum.NoExpectedFiles]: {
    title: 'No Expected Attachments',
    description:
      'No attachments are required for this step.  However, you can add any attachments that you wish and they will be included in the Docusign envelope.'
  },
  [InfoBannerTypeEnum.MissingFiles]: {
    title: 'Missing Attachments Detected',
    description:
      'Attachments will be added to the end of the signature packet and included in the Docusign envelope.  Expected or missing attachments are listed here as a warning.  But you can choose to skip any missing document warnings and add  additional attachments as you wish.'
  },
  [InfoBannerTypeEnum.AllFilesPresent]: {
    title: 'No Missing Attachments Detected',
    description:
      'Attachments will be added to the end of the signature packet and included in the Docusign envelope.  All expected attachments are already included.  But you can choose to add  additional attachments as you wish.'
  }
};

export const attachmentMetadataToolTipContent = (type: string, description: string) => {
  return (
    <TableBodyWrapper>
      <TableRowCell width={100}>
        <TextCell>{type}</TextCell>
      </TableRowCell>
      {description && (
        <TableRowCell width={100}>
          <TextCell>{description}</TextCell>
        </TableRowCell>
      )}
    </TableBodyWrapper>
  );
};

export const openDocumentationAttachmentFile = async (fileName: string, entityId: string) => {
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_BACKEND_BASE_URL}/legal-entity-attachment/legal-entity/${entityId}/attachments/${fileName}`,
      {
        headers: { authorization: `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      }
    );

    if (fileName.toLowerCase().endsWith('.pdf')) {
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      return;
    }

    if (fileName.toLowerCase().endsWith('.png')) {
      const url = window.URL.createObjectURL(new Blob([data], { type: 'image/png' }));
      window.open(url, '_blank');
      return;
    }

    if (fileName.toLowerCase().endsWith('.jpeg') || fileName.toLowerCase().endsWith('.jpg')) {
      const url = window.URL.createObjectURL(new Blob([data], { type: 'image/jpeg' }));
      window.open(url, '_blank');
      return;
    }

    fileDownload(data, fileName);
  } catch (error) {
    console.error(error);
  }
};

export const buildAttachmentSectionsWithFiles = (attachmentSections: AttachmentSection[], attachments: any): IAttachmentSectionWithFiles[] => {
  const sectionMap = new Map(attachmentSections.map((s) => [s.name, s]));
  const updatedSections: IAttachmentSectionWithFiles[] = [];

  attachmentSections.forEach((section) => {
    updatedSections.push({
      name: section.name,
      label: section.label,
      ...(section?.contactId && { contactId: section.contactId }),
      expectedAttachments: section.expectedAttachments
        .filter((expectedAttachment) => expectedAttachment.name !== 'type' && expectedAttachment.name !== 'contactId')
        .map((expectedAttachment) => {
          const attachedFiles = attachments?.[section.name]?.[expectedAttachment.name] || [];
          return {
            name: expectedAttachment.name,
            label: expectedAttachment.label,
            attachments: attachedFiles
          };
        }),
      initialExpectedAttachmentsCount: section.expectedAttachments?.length || 0,
      expectedAttachmentsCount: 0,
      additionalAttachmentsCount: 0
    });
  });

  Object.entries(attachments).forEach(([sectionName, sectionValue]) => {
    let section = sectionMap.get(sectionName);
    let updatedSection = updatedSections.find((s) => s.name === sectionName);

    if (!section) {
      section = { name: sectionName, label: sectionName, expectedAttachments: [] };
      updatedSection = {
        name: sectionName,
        label: sectionName,
        initialExpectedAttachmentsCount: section.expectedAttachments?.length || 0,
        expectedAttachmentsCount: 0,
        additionalAttachmentsCount: 0,
        expectedAttachments: []
      };
      updatedSections.push(updatedSection);
    }

    if (!sectionValue) return;

    Object.entries(sectionValue).forEach(([expectedAttachmentName, attachedFiles]) => {
      if (expectedAttachmentName === 'type' || expectedAttachmentName === 'contactId') return;

      let expectedAttachment = section.expectedAttachments?.find((ea) => ea.name === expectedAttachmentName);
      let updatedExpectedAttachment = updatedSection?.expectedAttachments.find((ea) => ea.name === expectedAttachmentName);

      if (!expectedAttachment) {
        expectedAttachment = { name: expectedAttachmentName, label: expectedAttachmentName };
        if (!updatedExpectedAttachment) {
          updatedSection?.expectedAttachments.push({
            name: expectedAttachmentName,
            label: AttachmentSubTypesEnum[expectedAttachmentName as keyof typeof AttachmentSubTypesEnum] || expectedAttachmentName,
            attachments: attachedFiles
          });
        }
      } else {
        if (updatedExpectedAttachment) {
          updatedExpectedAttachment.attachments = attachedFiles;
        }
      }
    });
  });

  updatedSections.forEach((section) => {
    const attachmentSection = attachmentSections.find((s) => s.name === section.name);

    let expectedAttachmentsCount = 0;
    let additionalAttachmentsCount = 0;

    section.expectedAttachments.forEach((expectedAttachment) => {
      const matchingAttachment = attachmentSection?.expectedAttachments?.find((ea) => ea.name === expectedAttachment.name);

      const uploadedCount = expectedAttachment.attachments?.length || 0;
      const requiredCount = matchingAttachment ? 1 : 0;

      if (uploadedCount > 0 && requiredCount > 0) {
        expectedAttachmentsCount += 1;
        additionalAttachmentsCount += uploadedCount - 1;
      } else {
        additionalAttachmentsCount += uploadedCount;
      }
    });

    section.expectedAttachmentsCount = expectedAttachmentsCount;
    section.additionalAttachmentsCount = additionalAttachmentsCount;
  });

  return updatedSections;
};

interface AttachmentsComponentProps {
  row: IRenderTemplateRow;
  mode: 'documentationTab' | 'wizard';
  pageName?: string;
  entityId: string;
  templateId?: string;
  ignoreRequired: boolean;
  contacts?: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['contacts'];
  documentationTabFormData: Record<string, any>;
  openModalPage: ({ type, row, id, currentAttachment, sectionName, expectedAttachmentName, contactId }: OpenWizardModalPageArgsType) => void;
}

export const AttachmentsComponent = ({
  row,
  mode,
  pageName = '',
  entityId,
  templateId,
  ignoreRequired,
  contacts,
  documentationTabFormData,
  openModalPage
}: AttachmentsComponentProps) => {
  const { isMobile } = useResponsive();
  const { name, dependsOn, config } = row;
  const {
    formState: { errors },
    getValues,
    setValue,
    unregister,
    watch
  } = useFormContext();

  const dependentValue = dependsOn ? watch(`${pageName}.${dependsOn.name}`) : null;
  const hasValidOrDependency: boolean = dependsOn?.or
    ? dependsOn.or.some((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;
  const hasValidAndDependency: boolean = dependsOn?.and
    ? dependsOn.and.every((item: any) => {
        const dependentValue = watch(`${pageName}.${item.name}`);
        return evaluateDependencyItem(dependentValue, item);
      })
    : null;

  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<IDocumentationAttachment[]>([]);
  const [currentFileData, setCurrentFileData] = useState({ fileName: '', sectionName: '', expectedAttachmentName: '' });
  const [attachmentSelectOptions, setAttachmentSelectOptions] = useState<ISelectValue[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [attachmentSectionsWithFiles, setAttachmentSectionsWithFiles] = useState<IAttachmentSectionWithFiles[]>([]);
  const [infoBannerType, setInfoBannerType] = useState<InfoBannerTypeEnum>(null);

  const [schema, attachmentSections] = processExpectedAttachments(formData, contacts, config);

  const [updateLegalEntityDocumentation] = useMutation(UPDATE_LEGAL_ENTITY_DOCUMENTATION_MUTATION);

  useEffect(() => {
    if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) {
      unregister(`${pageName}.${name}`);
    }
  }, [dependentValue, hasValidOrDependency, hasValidAndDependency]);

  useEffect(() => {
    if (mode !== 'wizard') return;

    if (!attachmentSections?.length) {
      setInfoBannerType(InfoBannerTypeEnum.NoExpectedFiles);
      return;
    }

    const attachmentsData = formData?.[pageName]?.[name] ?? {};
    const flattenedAttachmentsData = Object.entries(attachmentsData).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item, idx) => {
            acc[`${key}[${idx}]`] = item;
          });
        } else {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );

    const dataToValidate = { attachments: flattenedAttachmentsData };
    const result = schema.safeParse(dataToValidate);

    if (!result.success) {
      setInfoBannerType(InfoBannerTypeEnum.MissingFiles);
      return;
    }
    setInfoBannerType(InfoBannerTypeEnum.AllFilesPresent);
  }, [formData]);

  useEffect(() => {
    if (mode === 'documentationTab') {
      setFormData(documentationTabFormData);
      return;
    }
    if (mode === 'wizard') {
      const wizardFormData = getValues();
      setFormData(wizardFormData);
    }
  }, [uploadedFiles]);

  useEffect(() => {
    (async () => {
      try {
        setAttachmentsLoading(true);
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/legal-entity-attachment/legal-entity/${entityId}/attachments`, {
          headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        setUploadedFiles(data as IDocumentationAttachment[]);
      } catch (error) {
        console.error(error);
      } finally {
        setAttachmentsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const attachments = getValues(`${pageName}.${name}`) || {};
    // Flatten array values in attachments
    const flattenedAttachments = Object.entries(attachments).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item, idx) => {
            acc[`${key}[${idx}]`] = item;
          });
        } else {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );

    const updatedAttachmentSections = buildAttachmentSectionsWithFiles(attachmentSections, flattenedAttachments);
    setAttachmentSectionsWithFiles(updatedAttachmentSections);

    const attachedFileNames = updatedAttachmentSections.flatMap((section) =>
      section.expectedAttachments.flatMap((expectedAttachment) => expectedAttachment.attachments)
    );

    const selectOptions = uploadedFiles
      .filter((file) => !attachedFileNames.includes(file.name))
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((file) => createSimpleSelectOption(file.name));

    setAttachmentSelectOptions(selectOptions);
  }, [uploadedFiles, JSON.stringify(getValues(`${pageName}.${name}`))]);

  const openModalWindow = (type: string, fileName: string, sectionName: string, expectedAttachmentName: string) => {
    setModalWindow({ type, isOpen: true });
    setCurrentFileData({ fileName, sectionName, expectedAttachmentName });
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
    setCurrentFileData({ fileName: '', sectionName: '', expectedAttachmentName: '' });
  };

  const onChangeSelectValue = async (value: string) => {
    if (!value) return;

    const selectedAttachment = uploadedFiles.find((file) => file.name === value);
    if (!selectedAttachment) return;
    openModalPage({ type: 'add-an-attachment-file', row, currentAttachment: selectedAttachment });
  };

  const handleDeleteLocalAttachments = (fileName: string, sectionName?: string, attachmentName?: string) => {
    if (mode === 'documentationTab') {
      openModalWindow('delete-attachment', fileName, sectionName, attachmentName);
      return;
    }

    const expectedAttachments = watch(`${pageName}.${name}.${sectionName}.${attachmentName}`);
    const updatedAttachments = expectedAttachments?.filter((attachment: string) => attachment !== fileName);
    setValue(`${pageName}.${name}.${sectionName}.${attachmentName}`, updatedAttachments);
    if (!updatedAttachments?.length) {
      unregister(`${pageName}.${name}.${sectionName}.${attachmentName}`);
    }
    const wizardFormData = getValues();
    setFormData(wizardFormData);
  };

  const onAttachmentDeleted = async () => {
    setUploadedFiles((prevFiles) => prevFiles.filter((file) => file.name !== currentFileData.fileName));
    const expectedAttachments = watch(`${pageName}.${name}.${currentFileData.sectionName}.${currentFileData.expectedAttachmentName}`);
    const updatedAttachments = expectedAttachments?.filter((attachment: string) => attachment !== currentFileData.fileName);
    setValue(`${pageName}.${name}.${currentFileData.sectionName}.${currentFileData.expectedAttachmentName}`, updatedAttachments);

    const dataToUpdate = { ...getValues(`${pageName}.${name}`) };
    await updateLegalEntityDocumentation({
      variables: {
        input: {
          id: entityId,
          templateId,
          kyc: dataToUpdate
        }
      }
    });

    if (!updatedAttachments?.length) {
      unregister(`${pageName}.${name}.${currentFileData.sectionName}.${currentFileData.expectedAttachmentName}`);
    }
    closeModalWindow();
  };

  const onEditAttachment = (expectedAttachment: IExpectedAttachments, sectionName: string, contactId?: string) => {
    const currentAttachment = uploadedFiles?.find((file) => expectedAttachment?.attachments?.includes(file.name));

    openModalPage({
      type: 'add-an-attachment-file',
      row,
      currentAttachment,
      sectionName: sectionName,
      expectedAttachmentName: expectedAttachment.name,
      contactId: contactId ?? ''
    });
  };

  if (shouldRenderEmptyComponent(dependsOn, dependentValue, hasValidOrDependency, hasValidAndDependency)) return <></>;

  if (attachmentsLoading) return <Loader />;

  return (
    <div>
      {modalWindow.isOpen && modalWindow.type === 'delete-attachment' && currentFileData?.fileName && (
        <DeleteAttachment
          isOpen={modalWindow.isOpen}
          entityId={entityId}
          currentFileName={currentFileData.fileName}
          onAttachmentDeleted={onAttachmentDeleted}
          onClose={closeModalWindow}
        />
      )}
      <AttachmentsSectionsWrapper>
        {mode === 'wizard' && infoBannerType && (
          <Banner
            title={infoBannerProperies[infoBannerType].title}
            description={infoBannerProperies[infoBannerType].description}
            icon={<InfoIcon width={26} height={26} fill={themeVar('font.base')} />}
            bgColor={themeVar('layer.12')}
            color={themeVar('font.base')}
            dividerColor={themeVar('font.disabled')}
            closeIconColor={themeVar('font.action')}
            onClose={() => setInfoBannerType(null)}
          />
        )}
        {attachmentSectionsWithFiles?.map(
          (attachmentSection) =>
            !!attachmentSection?.expectedAttachments?.length && (
              <AttachmentsSection key={attachmentSection.name}>
                <AttachmentsSectionHeader>
                  <StyledHeaderComponent>{attachmentSection.label}</StyledHeaderComponent>
                  <p>
                    {attachmentSection.expectedAttachmentsCount}/{attachmentSection.initialExpectedAttachmentsCount} expected attachments{' '}
                    {!!attachmentSection.additionalAttachmentsCount && `(and ${attachmentSection.additionalAttachmentsCount} extra added)`}
                  </p>
                </AttachmentsSectionHeader>
                {attachmentSection?.expectedAttachments?.map((expectedAttachment) => (
                  <React.Fragment key={expectedAttachment.name}>
                    {expectedAttachment?.attachments?.length ? (
                      expectedAttachment?.attachments?.map((attachmentName: string, index: number) => (
                        <FileUpload
                          key={`${attachmentName}-${index}`}
                          type="document"
                          fileName={attachmentName}
                          withHover
                          additionalContent={<Tag title={expectedAttachment.label} size="sm" />}
                          customActionButtons={
                            <>
                              <OpenAttachmentWrapper id="infoWrap" onClick={() => openDocumentationAttachmentFile(attachmentName, entityId)}>
                                <OpenFileIcon fill={themeVar('font.action')} />
                              </OpenAttachmentWrapper>
                              <DropdownActions
                                items={[
                                  {
                                    name: 'Edit',
                                    onClick: () => onEditAttachment(expectedAttachment, attachmentSection.name, attachmentSection?.contactId)
                                  },
                                  {
                                    name: 'Delete',
                                    onClick: () => handleDeleteLocalAttachments(attachmentName, attachmentSection.name, expectedAttachment.name)
                                  }
                                ]}
                              />
                            </>
                          }
                        />
                      ))
                    ) : (
                      <AddAttachmentContainer>
                        <FileUploadContainer>
                          <Tag title={expectedAttachment.label} size="sm" />
                        </FileUploadContainer>
                        <AddAttachmentButton
                          onClick={() =>
                            openModalPage({
                              type: 'add-an-attachment-file',
                              row,
                              sectionName: attachmentSection.name,
                              expectedAttachmentName: expectedAttachment.name,
                              contactId: attachmentSection?.contactId ?? ''
                            })
                          }
                        >
                          <PlusIcon width={20} height={20} fill={themeVar('action.default')} />
                        </AddAttachmentButton>
                      </AddAttachmentContainer>
                    )}
                  </React.Fragment>
                ))}
              </AttachmentsSection>
            )
        )}
      </AttachmentsSectionsWrapper>
      <Divider />
      <ThreeColumnWrapper isMobile={isMobile}>
        <Select
          label="Add an Attachment File"
          options={[{ label: 'Select', value: '' }, ...attachmentSelectOptions]}
          setValue={onChangeSelectValue}
          errorText={(errors as any)?.[pageName]?.[name]?.message?.toString()}
          isRequiredField={!ignoreRequired}
          size="md"
          additionalOption={
            <AddNewAttachmentButtonWrapper>
              <Button size="sm" styleType="outline" onClick={() => openModalPage({ type: 'add-an-attachment-file', row })}>
                Add New Attachment
              </Button>
            </AddNewAttachmentButtonWrapper>
          }
        />
      </ThreeColumnWrapper>
    </div>
  );
};

const AddNewAttachmentButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  margin: 20px 0;
  background-color: ${themeVar('border.base')};
`;

const AttachmentsSectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const AttachmentsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AttachmentsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AddAttachmentContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-right: 20px;
`;

const FileUploadContainer = styled.div`
  width: 100%;
  padding: 18px 40px;
  border-radius: 5px;
  border: 1px dashed ${themeVar('action.default')};
  background-color: ${themeVar('layer.12')};
`;

const AddAttachmentButton = styled.div`
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid ${themeVar('action.default')};
  background-color: ${themeVar('layer.base')};
`;

const OpenAttachmentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 40px;
  width: 40px;
  opacity: 0;
  border-radius: 4px;
  border: 1px solid ${themeVar('font.action')};
  background: ${themeVar('layer.base')};
`;
